import {
  Activity,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileCheck,
  FileText,
  LayoutDashboard,
  LogOut,
  MapPin,
  MessageSquare,
  Play,
  Settings,
  Shield,
  TrendingUp,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../constants/permissions";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useTheme } from "../../context/ThemeContext";

export default function Sidebar({ collapsed, onToggle }) {
  // "hotel" |"retail"|"restaurant"|"ecommerce"
  const { routeState } = useTheme();
  const navItems = [
    { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    {
      name:
        routeState === "hotel"
          ? "Booking Management"
          : routeState === "retail"
            ? "Sales Mangement"
            : routeState === "restaurants"
              ? "Order Management"
              : "Order Management",
      icon: MapPin,
      path: "/trips",
    },
    { name: "Demo", icon: Play, path: "/demo" },
    {
      name: "Advance Analytics",
      icon: BarChart3,
      path: "/analytics",
      permission: true || PERMISSIONS.ANALYTICS_VIEW,
    },
    {
      name: "Market Analysis",
      icon: TrendingUp,
      path: "/customers",
      permission: true || PERMISSIONS.ANALYTICS_VIEW,
    },
    {
      name: "Pricing Review",
      icon: FileCheck,
      path: "/conversion",
      permission: true || PERMISSIONS.PRICING_VIEW,
    },
    { name: "System Performance", icon: Activity, path: "/performance" },
    {
      name: "Settings",
      icon: Settings,
      path: "/settings",
      permission: true || PERMISSIONS.SETTINGS_VIEW,
    },
    { name: "Support & Feedback", icon: MessageSquare, path: "/support" },
    {
      name: "Subscription",
      icon: DollarSign,
      path: "/subscription",
      permission: true || PERMISSIONS.BILLING_VIEW,
    },
    {
      name: "Invoices",
      icon: FileText,
      path: "/invoices",
      permission: true || PERMISSIONS.BILLING_VIEW,
    },
  ];
  const { logout } = useAuth();
  const { currentTenant } = useTenant();
  const { can, isPlatformAdmin } = usePermissions();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Filter nav items based on permissions
  // const filteredNavItems = navItems.filter(
  //   (item) => !item.permission || can(item.permission),
  // );
  const filteredNavItems = navItems;

  // Get tenant branding
  const brandColor = currentTenant?.branding?.primary_color || "#2563eb";
  const tenantName = currentTenant?.name || "ServiceHive";
  const tenantInitial = tenantName[0] || "S";

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col z-40 transition-all duration-300 ${collapsed ? "w-20" : "w-64"}`}
    >
      {/* Logo with Tenant Branding */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)`,
            }}
          >
            <span className="text-white font-bold text-lg">
              {tenantInitial}
            </span>
          </div>
          {!collapsed && (
            <div>
              <span className="font-semibold text-xl text-gray-800 dark:text-white">
                {tenantName}
              </span>
              {currentTenant?.plan && (
                <span className="block text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {currentTenant.plan} Plan
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-50"
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 overflow-y-auto">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.path}
                title={collapsed ? item.name : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/50"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  } ${collapsed ? "justify-center" : ""}`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <span className="whitespace-nowrap">{item.name}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Admin Console Link (for platform admins) */}
        {isPlatformAdmin && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
            <NavLink
              to="/admin"
              title={collapsed ? "Admin Console" : undefined}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors ${collapsed ? "justify-center" : ""}`}
            >
              <Shield className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>Admin Console</span>}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Sign Out */}
      <div className="p-3 border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={handleLogout}
          title={collapsed ? "Sign Out" : undefined}
          className={`flex items-center gap-3 px-3 py-3 w-full rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${collapsed ? "justify-center" : ""}`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );
}
